import type React from "react";
import { useState } from "react";
import { Box, Tabs, Tab, Typography, Avatar, Paper } from "@mui/material";
import { PlayCircleOutline } from "@mui/icons-material";
import type { IStreamDetails, IThemeConfig } from "./types/memorial-types";
import MemoriesTab from "./tabs/MemoriesTab";
import CommentCard from "./CommentCard";
import { IColorTokens } from "../../../../../types/theme";

interface ICommentType {
  id: string;
  author: string;
  date: string;
  content: string;
  is_approved?: boolean;
  replies?: ICommentType[];
  parent_message?: string;
}

interface ConferenceSidePanelProps {
  streams: IStreamDetails[];
  activeStream: number;
  onStreamChange: (streamIndex: number) => void;
  memorialTheme: IThemeConfig;
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.MutableRefObject<WebSocket | null>;
  isMobile: boolean;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  colors: IColorTokens;
}

const ConferenceSidePanel: React.FC<ConferenceSidePanelProps> = ({
  streams,
  activeStream,
  onStreamChange,
  memorialTheme,
  messages,
  isAdmin,
  socketRef,
  isMobile,
  handleApprove,
  handleDelete,
  colors,
}) => {
  const [tab, setTab] = useState(0);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timeStr?: string | null) => {
    if (!timeStr) return "";

    const [hours = "00", minutes = "00"] = timeStr.split(":");

    return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  };

  const getYouTubeThumbnail = (url?: string | null) => {
    if (!url) return null;
    const regExp =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regExp);
    const videoId = match?.[1];
    return videoId
      ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
      : null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",

        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        backgroundColor: colors.primary[900],
      }}
    >
      {/* Tab Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box
          sx={{
            backgroundColor: "action.hover",
            borderRadius: "50px",
            p: 0.5,
            display: "flex",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              width: "100%",
              minHeight: 40,
              "& .MuiTabs-flexContainer": { gap: 0 },
              "& .MuiTab-root": {
                flex: 1,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.9rem",
                minHeight: 40,
                borderRadius: "50px",
                color: "#888",
                "&.Mui-selected": {
                  color: colors.primary[700],
                  fontWeight: 600,
                  backgroundColor: colors.primary[900],
                },
              },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            <Tab label="Comments" />
            <Tab label="Streams" />
          </Tabs>
        </Box>
      </Box>
      {/* Tab Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {tab === 0 && (
          <Box sx={{ p: 1 }}>
            <MemoriesTab
              messages={messages}
              isAdmin={isAdmin}
              socketRef={socketRef}
              isMobile={isMobile}
              handleApprove={handleApprove}
              handleDelete={handleDelete}
              CommentCard={CommentCard}
              colors={colors}
            />
          </Box>
        )}
        {tab === 1 && (
          <Box>
            {streams.map((stream, index) => {
              const isActive = index === activeStream;
              return (
                <Box
                  key={stream.id}
                  onClick={() => onStreamChange(index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    backgroundColor: isActive
                      ? `${memorialTheme.primary}10`
                      : "transparent",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isActive
                        ? `${memorialTheme.primary}18`
                        : "#f5f5f5",
                    },
                    //borderBottom: "1px solid",
                    //borderBottomColor: "divider",
                  }}
                >
                  {/* Thumbnail */}
                  <Box
                    sx={{
                      position: "relative",
                      flexShrink: 0,
                      width: 72,
                      height: 52,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    {(() => {
                      const thumb =
                        stream.stream_thumbnail_background_image ||
                        getYouTubeThumbnail(stream.youtube_stream_link);
                      return thumb ? (
                        <Box
                          component="img"
                          src={thumb}
                          alt={stream.title}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: memorialTheme.primary + "22",
                            borderRadius: 0,
                          }}
                        >
                          <PlayCircleOutline
                            sx={{ color: memorialTheme.primary, fontSize: 28 }}
                          />
                        </Avatar>
                      );
                    })()}

                    {/* Active play indicator overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: isActive
                          ? `${memorialTheme.primary}55`
                          : "rgba(0,0,0,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlayCircleOutline
                        sx={{
                          color: "#fff",
                          fontSize: 22,
                          opacity: isActive ? 0.7 : 1,
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 700 : 500}
                      noWrap
                      sx={{
                        color: isActive
                          ? memorialTheme.primary
                          : "text.primary",
                        fontSize: "0.85rem",
                        lineHeight: 1.3,
                        mb: 0.5,
                      }}
                    >
                      {stream.title}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.3,
                      }}
                    >
                      {/* <CalendarToday sx={{ fontSize: 11, color: "text.secondary" }} /> */}
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontSize: "0.72rem" }}
                      >
                        {formatDate(stream.date)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {/* <AccessTime sx={{ fontSize: 11, color: "text.secondary" }} /> */}
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontSize: "0.72rem" }}
                      >
                        {formatTime(stream.start)}
                        {stream.end ? ` – ${formatTime(stream.end)}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ConferenceSidePanel;
